import { Product as ProtoProduct } from '@italist/grpc-proto/entities/product_pb';
import * as cloudImage from '../../server/helpers/cloudImage';
import { Product } from '../../models/product';
import Departments from '../../models/departments';
import { ProductSnapshot } from '../../models/cart';
import { decorateBrand } from './brands';
import { decorateCategory } from './categories';

const formatSeason = (productSeason: ProtoProduct.Season): string => {
  const canonical = {
    AI: 'FW',
    FW: 'FW',
    PE: 'SS',
    SS: 'SS',
  };
  const season =
    canonical[
      productSeason
        .getSeason()
        .toUpperCase()
        .replace(/[\s/]/g, '')
    ];
  const year = parseInt(productSeason.getYear().replace(/\D/g, ''), 10);
  if (!season || !year) {
    return '';
  }
  if (season === 'FW') {
    return `F${(year % 100).toString().padStart(2, '0')} - W${((year % 100) + 1).toString().padStart(2, '0')}`;
  }
  return `S${(year % 100).toString().padStart(2, '0')} - S${(year % 100).toString().padStart(2, '0')}`;
};

export const formatSeasonFromString = (season: string): string => {
  const [name, year] = season.match(/[a-zA-Z]+|[0-9]+/g);
  const [first, second] = name.split('');
  const parsedYear = parseInt(year, 10);
  return `${first}${parsedYear} - ${second}${name === 'SS' ? parsedYear : parsedYear + 1}`;
};

export const decorateProduct = (productProto: ProtoProduct): Product => {
  const product = productProto.toObject();
  const protoCategoryList = productProto.getCategoriesList();
  const categoryPath = protoCategoryList.map(protoCategory => decorateCategory(protoCategory, protoCategoryList));

  const sizesList = product.sizesList.filter(size => size.numberAvailable >= 1);
  const category = categoryPath[0];
  return {
    ...product,
    brand: decorateBrand(productProto.getBrand()),
    category,
    categoryPath,
    formattedSeason: formatSeason(productProto.getSeason()),
    images: product.imagesList.map(image => cloudImage.getAllTransformations(image.url)),
    isActive: product.status === ProtoProduct.Status.AVAILABLE,
    isExcluded: product.status === ProtoProduct.Status.EXCLUDED,
    sizesList,
  };
};

export const isSnapshot = (product: Product | ProductSnapshot): product is ProductSnapshot => {
  if ((product as ProductSnapshot).department) {
    return true;
  }
  return false;
};

export const getProductFilteredCategories = (product: Product | ProductSnapshot, userFacingDepartment?: string) => {
  const department = isSnapshot(product) ? product.department : product.departmentsList[0].internalName;
  if (Departments.isUnisexDepartment(department)) {
    const nonUnisexDept = userFacingDepartment || Departments.getNonUnisexDepartment(department);
    return product.categoryPath.filter(cat => cat.department.internalName === nonUnisexDept);
  }
  return product.categoryPath;
};

export const getProductSearchCategories = (product: Product | ProductSnapshot) => {
  return getProductFilteredCategories(product)
    .slice()
    .reverse()
    .filter(value => parseInt(value.presentationWeight, 10) >= 0 && parseInt(value.presentationWeight, 10) <= 3);
};
